import { ContentLoader, PlayerPlaylistChangeArgs, PlayerPlaylistItemChangeArgs } from '../../media-player';
import { EventEmitterBase } from '../../utils';
import { TypeGuards } from '../type-guards';
import { PlaylistControllerEvents } from './playlist-controller-events';
import { MediaItem } from './setup';

export class PlaylistController extends EventEmitterBase {

    playlist: MediaItem[] = [];
    currentIndex: number = -1;

    constructor() {
        super();
    }

    async load(playlist: MediaItem[] | MediaItem | string): Promise<MediaItem[]> {
        if (typeof playlist === 'string') {
            return await this.load(await ContentLoader.fetch(playlist));
        }
        if (TypeGuards.isMediaItem(playlist)) {
            return await this.load([playlist]);
        }
        if (TypeGuards.isMediaPlaylist(playlist)) {
            this.playlist = playlist;
            this.currentIndex = -1;
            const event: PlayerPlaylistChangeArgs = {
                playlist: this.playlist,
            };
            this.emit(PlaylistControllerEvents.PlaylistChange, event);
            this.setCurrentIndex(0);
            return playlist;
        }
    }

    get currentItem(): MediaItem {
        return this.playlist[this.currentIndex];
    }

    getItem(index: number): MediaItem {
        if (index > -1 && index < this.playlist.length) {
            return this.playlist[index];
        }
        return null;
    }

    setCurrentIndex(index: number): void {
        if (this.playlist.length > 0
            && index !== this.currentIndex
            && typeof index === 'number'
            && index >= 0
            && index < this.playlist.length) {
            this.currentIndex = index;
            const event: PlayerPlaylistItemChangeArgs = {
                index: this.currentIndex,
                item: this.currentItem,
            };
            this.emit(PlaylistControllerEvents.PlaylistItemChange, event);
        }
    }

    hasNext(): boolean {
        return this.playlist.length > 0 && this.currentIndex < (this.playlist.length - 1);
    }

    hasPrev(): boolean {
        return this.playlist.length > 0 && this.currentIndex > 0;
    }

    prev(): void {
        const prev: number = this.getPrev();
        if (prev) {
            this.setCurrentIndex(prev);
        }
    }

    next(): void {
        const next: number = this.getNext();
        if (next) {
            this.setCurrentIndex(next);
        }
    }

    getNext(): number {
        if (this.hasNext()) {
            return this.currentIndex + 1;
        }
        return null;
    }

    getPrev(): number {
        if (this.hasPrev()) {
            return this.currentIndex - 1;
        }
        return null;
    }

    reset(): void {
        this.currentIndex = -1;
        this.playlist = [];
        this.removeAllListeners();
    }
}
